import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { me } from "../lib/api";
import { useAuthStore } from "../state/authStore";
import { useQuizStore } from "../state/store";

const NAME_STORAGE_KEY = "ppl-certificate-name";

/** Silent fire-and-forget for the #91 completion funnel — failures must never
 *  block the certificate view or print. */
function trackCertificateEvent(kind: "view" | "print"): void {
  void me.recordCertificateEvent(kind).catch(() => undefined);
}

type CertType = "en_de" | "de_en" | "master";

interface CertVariant {
  title: string;
  eyebrow: string;
  subtitle: string;
  body: (count: number) => JSX.Element;
  sealTopLabel: string;
  sealBottomLabel: string;
}

function buildVariant(type: CertType): CertVariant {
  switch (type) {
    case "en_de":
      return {
        title: "Zertifikat",
        eyebrow: "Teilnahme-Bestätigung",
        subtitle: "Englisch → Deutsch · vollständige Bearbeitung des Vokabel-Trainers",
        body: (count) => (
          <>
            hat den Vokabel-Trainer von <strong>PPLearning</strong> in der Lernrichtung{" "}
            <strong>Englisch → Deutsch</strong> erfolgreich durchlaufen und alle{" "}
            <span className="highlight" data-testid="certificate-vocab-count">
              {count}
            </span>{" "}
            Vokabeln aus dem Wortschatz der englischen Sprachprüfung für die Privatpilotenlizenz
            mindestens einmal <span className="highlight">vollständig korrekt</span> beantwortet.
          </>
        ),
        sealTopLabel: "EN → DE",
        sealBottomLabel: "Vokabeln",
      };
    case "de_en":
      return {
        title: "Zertifikat",
        eyebrow: "Teilnahme-Bestätigung",
        subtitle: "Deutsch → Englisch · vollständige Bearbeitung des Vokabel-Trainers",
        body: (count) => (
          <>
            hat den Vokabel-Trainer von <strong>PPLearning</strong> in der aktiven Abruf-Richtung{" "}
            <strong>Deutsch → Englisch</strong> erfolgreich durchlaufen und für alle{" "}
            <span className="highlight" data-testid="certificate-vocab-count">
              {count}
            </span>{" "}
            Vokabeln das passende englische Fachwort mindestens einmal{" "}
            <span className="highlight">vollständig korrekt</span> gewählt.
          </>
        ),
        sealTopLabel: "DE → EN",
        sealBottomLabel: "Vokabeln",
      };
    case "master":
      return {
        title: "Master-Zertifikat",
        eyebrow: "Master-Bestätigung",
        subtitle: "Beide Richtungen vollendet — bidirektional gemastert",
        body: (count) => (
          <>
            hat den Vokabel-Trainer von <strong>PPLearning</strong> in{" "}
            <strong>beiden Lernrichtungen</strong> (Englisch ↔ Deutsch) erfolgreich durchlaufen und
            alle{" "}
            <span className="highlight" data-testid="certificate-vocab-count">
              {count}
            </span>{" "}
            Vokabeln <span className="highlight">beidseitig vollständig korrekt</span>{" "}
            beantwortet — die höchste Auszeichnung der App.
          </>
        ),
        sealTopLabel: "Master",
        sealBottomLabel: "Vokabeln",
      };
  }
}

/**
 * Print-friendly Zertifikat-Page (Issue #89 + Phase 4 von #95).
 *
 * Issue #95 Phase 4: 3 Varianten je nach erreichter Lernrichtung.
 *  - 0 verfügbar → "Noch nicht ganz fertig"-State (wie heute)
 *  - 1 verfügbar → direkt rendern
 *  - ≥ 2 verfügbar → Chooser mit Auswahlkarten, dann das gewählte Zertifikat
 *
 * Layout, Logo, Stempel-Position und Disclaimer identisch über alle Varianten.
 * Variante unterscheidet sich in: Titel, Subtitel, Body-Text, Siegel-Beschriftung.
 */
export function CertificatePage(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const progress = useQuizStore((s) => s.progress);
  const vocab = useQuizStore((s) => s.vocab);

  const [name, setName] = useState("");
  const viewTrackedRef = useRef(false);
  const [selectedCert, setSelectedCert] = useState<CertType | null>(null);

  // Compute which certificates are eligible: per direction, all active vocab
  // must be mastered in that direction. Master = both directions.
  const { masterReady, available } = useMemo(() => {
    let enMastered = 0;
    let deMastered = 0;
    for (const p of progress.values()) {
      if (p.state === "mastered") {
        if (p.direction === "en_de") enMastered++;
        else deMastered++;
      }
    }
    const total = vocab.length;
    const en = total > 0 && enMastered === total;
    const de = total > 0 && deMastered === total;
    const m = en && de;
    const av: CertType[] = [];
    if (en) av.push("en_de");
    if (de) av.push("de_en");
    if (m) av.push("master");
    return { masterReady: m, available: av };
  }, [progress, vocab.length]);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(NAME_STORAGE_KEY);
      if (saved) setName(saved);
    } catch {
      // Storage blocked — silent fail.
    }
  }, []);

  // Default-Auswahl: Master falls verfügbar, sonst das erste verfügbare.
  useEffect(() => {
    if (selectedCert === null && available.length > 0) {
      setSelectedCert(masterReady ? "master" : (available[0] ?? null));
    }
  }, [available, masterReady, selectedCert]);

  useEffect(() => {
    if (available.length > 0 && !viewTrackedRef.current) {
      viewTrackedRef.current = true;
      trackCertificateEvent("view");
    }
  }, [available.length]);

  function updateName(value: string) {
    setName(value);
    try {
      window.localStorage.setItem(NAME_STORAGE_KEY, value);
    } catch {
      /* no persistence */
    }
  }

  const today = new Date();
  const dateLabel = today.toLocaleDateString("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  function onPrint() {
    trackCertificateEvent("print");
    window.print();
  }

  // Kein Zertifikat verfügbar → Motivations-State.
  if (available.length === 0) {
    return (
      <main className="min-h-full">
        <div className="mx-auto w-full max-w-md px-4 pt-10 sm:pt-16 safe-top text-center space-y-4">
          <p className="text-6xl" aria-hidden>
            📜
          </p>
          <h1 className="text-2xl font-bold tracking-tight text-fg">
            Noch nicht ganz fertig
          </h1>
          <p className="text-fg-muted leading-relaxed">
            Sobald du alle Vokabeln in mindestens einer Lernrichtung gemeistert hast, wartet hier
            dein Teilnahme-Zertifikat zum Ausdrucken. Master-Zertifikat gibt's bei beiden Richtungen.
          </p>
          <div className="text-xs text-fg-faint tabular">
            EN → DE: {progress.size > 0 ? Array.from(progress.values()).filter((p) => p.direction === "en_de" && p.state === "mastered").length : 0} / {vocab.length}
            {"  ·  "}
            DE → EN: {progress.size > 0 ? Array.from(progress.values()).filter((p) => p.direction === "de_en" && p.state === "mastered").length : 0} / {vocab.length}
          </div>
          <Link
            to="/"
            className="inline-block min-h-[44px] px-4 py-2 rounded-lg text-accent hover:underline"
          >
            ← Zum Quiz
          </Link>
        </div>
      </main>
    );
  }

  const displayedRecipient = name.trim().length > 0 ? name.trim() : user?.email ?? "—";
  const showChooser = available.length >= 2;
  const certType: CertType = selectedCert ?? available[0]!;
  const variant = buildVariant(certType);

  return (
    <main className="min-h-full" data-testid="certificate-page">
      <div className="cert-actions print:hidden mx-auto w-full max-w-[1123px] px-4 pt-6 flex items-center justify-between gap-3 safe-top">
        <Link
          to="/"
          className="min-h-[44px] inline-flex items-center px-3 py-2 rounded-lg text-sm text-fg-muted hover:text-fg hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong"
        >
          ← Zurück
        </Link>
        <button
          type="button"
          onClick={onPrint}
          data-testid="certificate-print"
          className="min-h-[44px] px-4 rounded-lg bg-accent text-accent-on text-sm font-semibold hover:bg-accent-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong"
        >
          🖨 Als PDF speichern / drucken
        </button>
      </div>

      {showChooser && (
        <div
          className="print:hidden mx-auto w-full max-w-[1123px] px-4 pt-4 space-y-2"
          data-testid="certificate-chooser"
        >
          <div className="text-xs uppercase tracking-widest text-fg-faint">
            Du hast {available.length} Zertifikate erreicht — wähle eins zum Anschauen / Drucken
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {available.map((type) => (
              <CertOptionButton
                key={type}
                type={type}
                active={certType === type}
                onClick={() => setSelectedCert(type)}
              />
            ))}
          </div>
        </div>
      )}

      <div className="cert-tips print:hidden mx-auto w-full max-w-[1123px] px-4 pt-4 space-y-3">
        <label className="block">
          <span className="text-xs uppercase tracking-widest text-fg-faint">
            Dein Name auf dem Zertifikat
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => updateName(e.target.value)}
            placeholder={user?.email ?? "Vor- und Nachname"}
            data-testid="certificate-name-input"
            className="mt-1 w-full max-w-md min-h-[44px] bg-bg border border-outline rounded-lg px-3 text-sm text-fg placeholder:text-fg-faint focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
          />
          <span className="block text-xs text-fg-faint mt-1">
            Wird in deinem Browser gespeichert und beim nächsten Mal automatisch vorbelegt. Leer
            lassen → E-Mail wird verwendet.
          </span>
        </label>

        <p className="text-xs text-fg-muted leading-relaxed bg-accent-soft border-l-[3px] border-accent rounded-r-md px-3 py-2.5">
          <strong className="text-fg">Tipp zum Drucken:</strong> wähle im Druck-Dialog{" "}
          <strong>„Querformat"</strong>, Skalierung <strong>„Standard / 100 %"</strong> und
          aktiviere falls vorhanden <strong>„Hintergrundgrafiken drucken"</strong> — sonst
          fehlen Brand-Farben und der Stempel.
        </p>
      </div>

      <div className="cert-wrap mx-auto w-full max-w-[1123px] px-4 py-6">
        <article
          className="certificate"
          data-testid="certificate-canvas"
          data-cert-type={certType}
          aria-label={variant.title}
        >
          <header className="cert-header">
            <div className="cert-brand">
              <img
                src="/brand/wordmark-horizontal-light.png"
                alt="PPLearning"
                className="cert-brand-logo"
              />
              <div className="cert-tagline">English Trainer for Private Pilot Licence</div>
            </div>
            <div className="cert-date">
              <div className="cert-date-label">Ausgestellt am</div>
              <div className="cert-date-value" data-testid="certificate-date">
                {dateLabel}
              </div>
            </div>
          </header>

          <div className="cert-title-block">
            <div className="cert-eyebrow">{variant.eyebrow}</div>
            <h1 className="cert-title" data-testid="certificate-title">
              {variant.title}
            </h1>
            <p className="cert-subtitle">{variant.subtitle}</p>
          </div>

          <div className="cert-body">
            <div className="cert-recipient-label">Verliehen an</div>
            <div className="cert-recipient" data-testid="certificate-recipient">
              {displayedRecipient}
            </div>
            <p className="cert-text">{variant.body(vocab.length)}</p>
          </div>

          <footer className="cert-footer">
            <div className="cert-stamp" data-testid="certificate-stamp">
              <div className="cert-stamp-inner">
                <span className="cert-stamp-corner cert-stamp-corner-tl" aria-hidden></span>
                <span className="cert-stamp-corner cert-stamp-corner-tr" aria-hidden></span>
                <span className="cert-stamp-corner cert-stamp-corner-bl" aria-hidden></span>
                <span className="cert-stamp-corner cert-stamp-corner-br" aria-hidden></span>
                <div className="cert-stamp-title">PPLearning</div>
                <div className="cert-stamp-divider"></div>
                <div className="cert-stamp-line1">
                  {certType === "master" ? "Master · Authentisch" : "Geprüft · Authentisch"}
                </div>
                <div className="cert-stamp-line2">Concept4IT Consulting GmbH</div>
              </div>
            </div>
            <div className="cert-seal">
              <span>{variant.sealTopLabel}</span>
              <span className="cert-seal-number">
                {vocab.length} / {vocab.length}
              </span>
              <span>{variant.sealBottomLabel}</span>
            </div>
          </footer>

          <div className="cert-disclaimer">
            Diese Bestätigung dokumentiert ausschließlich die Teilnahme am privaten Lernwerkzeug
            PPLearning und ist <strong>kein</strong> offizielles Sprachzeugnis. Sie ersetzt nicht
            die offizielle Sprachprüfung (Language Proficiency Check) beim Luftfahrt-Bundesamt
            bzw. einer anerkannten Prüfungsstelle. Siehe Haftungsausschluss unter
            pplearning.de/haftung.
          </div>
        </article>
      </div>
    </main>
  );
}

function CertOptionButton({
  type,
  active,
  onClick,
}: {
  type: CertType;
  active: boolean;
  onClick: () => void;
}) {
  const labels: Record<CertType, { title: string; sub: string; icon: string }> = {
    en_de: { title: "Englisch → Deutsch", sub: "Kurs vollendet", icon: "✓" },
    de_en: { title: "Deutsch → Englisch", sub: "Kurs vollendet", icon: "✓" },
    master: { title: "Master", sub: "Beide Richtungen", icon: "★" },
  };
  const info = labels[type];
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={`certificate-option-${type}`}
      data-cert-type={type}
      className={`min-h-[60px] px-3 py-2.5 rounded-xl border text-left transition-colors flex items-center gap-3 ${
        active
          ? "bg-accent-soft border-accent text-fg"
          : "bg-card border-outline-soft text-fg-soft hover:bg-bg/40"
      }`}
    >
      <span
        aria-hidden
        className={`inline-flex items-center justify-center w-10 h-10 rounded-lg text-lg font-bold ${
          active ? "bg-accent text-accent-on" : "bg-bg/40 text-fg-muted"
        }`}
      >
        {info.icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold leading-tight">{info.title}</span>
        <span className="block text-xs text-fg-muted leading-tight mt-0.5">{info.sub}</span>
      </span>
    </button>
  );
}
